import { useTenants } from '@api/tenants';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid
} from '@mui/material';
import {
	MAX_CHARS_TENANT_ID,
	stringNotAnyValidationScheme
} from '@utils/validation';
import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import { IDialogProps } from 'types/common';
import { ITenantCreationFields } from 'types/settings';
import { ITenant } from 'types/tenants';
import { array, object } from 'yup';
import TenantAddDomains from './TenantAddDomains';

const FORM_KEYS = {
	name: 'name',
	id: 'id',
	selfProvisioningDomains: 'selfProvisioningDomains',
	logo: 'logo'
};

const TenantCreateDialog = ({
	onSubmit,
	onCancel
}: IDialogProps<ITenantCreationFields, ITenant | undefined>) => {
	const { data } = useTenants();

	const tenantNames = useMemo(
		() => (data?.tenants || []).map((tenant: { name: string }) => tenant.name),
		[data]
	);

	const tenantIDs = useMemo(
		() => (data?.tenants || []).map((tenant: { id: string }) => tenant.id),
		[data]
	);

	const tenantDomains = useMemo(
		() =>
			(data?.tenants || []).reduce(
				(acc: string[], tenant: { selfProvisioningDomains?: string[] }) => [
					...acc,
					...(tenant.selfProvisioningDomains || [])
				],
				[]
			),
		[data]
	);

	const formik = useFormik<ITenantCreationFields>({
		initialValues: {
			name: '',
			logo: '',
			id: '',
			selfProvisioningDomains: []
		},
		validateOnBlur: true,
		validationSchema: object({
			[FORM_KEYS.name]: stringNotAnyValidationScheme(
				true,
				tenantNames,
				'tenants.creation.dialog.input.name.unique'
			),
			[FORM_KEYS.id]: stringNotAnyValidationScheme(
				false,
				tenantIDs,
				'tenants.creation.dialog.input.id.unique'
			).max(
				MAX_CHARS_TENANT_ID,
				intl.tr('validations.field.characters.maximum', {
					max: MAX_CHARS_TENANT_ID
				})
			),
			[FORM_KEYS.selfProvisioningDomains]: array().of(
				stringNotAnyValidationScheme(
					false,
					tenantDomains,
					'tenants.creation.dialog.input.domain.unique'
				)
			)
		}),
		onSubmit
	});

	return (
		<Dialog open maxWidth="sm" disableRestoreFocus>
			<DialogTitle>{intl.tr('tenants.creation.dialog.title')}</DialogTitle>
			<DialogContent>
				<Box pt={2} pb={2}>
					<Grid container spacing={2}>
						<Grid item container xs={12}>
							<Grid item xs={12} pb={2}>
								<TextFieldValidation
									id={FORM_KEYS.name}
									formik={formik}
									required
									fullWidth
									autoFocus
									label={intl.tr('tenants.creation.dialog.input.name.label')}
								/>
							</Grid>
							<Grid item xs={12} pb={2}>
								<TextFieldValidation
									id={FORM_KEYS.id}
									formik={formik}
									fullWidth
									label={intl.tr('tenants.creation.dialog.input.id.label')}
								/>
							</Grid>
							<Grid item xs={12}>
								<TenantAddDomains formik={formik} />
							</Grid>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions sx={{ paddingRight: 3, paddingBottom: 3 }}>
				<Button
					onClick={onCancel}
					data-testid="tenant-create-dialog-cancel-button"
				>
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					disabled={!formik.isValid}
					loading={formik.isSubmitting}
					onClick={() => formik.handleSubmit()}
					data-testid="tenant-create-approve-button"
				>
					{intl.tr('button.create')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default TenantCreateDialog;
